/* eslint-disable react/no-array-index-key */
import React, { lazy, Suspense, Fragment } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import DashboardLayout from 'src/layouts/DashboardLayout';
import MainLayout from 'src/layouts/MainLayout';
import LoadingScreen from 'src/components/LoadingScreen';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';
import UserPermissionGuard from 'src/components/UserPermissionGuard';
import CPTextingGuard from 'src/components/CPTextingGuard';
import MessageView from 'src/views/Settings/MessageView/index';
import HelpAndInformation from 'src/views/pages/HelpAndInformation';
import ChatView from 'src/views/management/ChatView';
import ScheduledMessagesView from 'src/views/management/ScheduledMessagesView';
import RedirectLogic from 'src/RedirectLogic';

import {
  ROUTES,
  PERMISSIONS,
} from 'src/constants';

const { PERMISSIONS_SYSTEM_CODE } = PERMISSIONS;

const routesConfig = [
  {
    exact: true,
    path: '/f/:formId',
    component: lazy(() => import('src/forms'))
  },
  {
    exact: true,
    path: '/o/:slug',
    component: lazy(() => import('src/textingOptIn'))
  },
  {
    exact: true,
    path: '/host',
    component: lazy(() => import('src/views/HostAppDownload'))
  },
  {
    exact: true,
    path: '/signupnow/:referralCode?',
    component: lazy(() => import('src/signupMUI6'))
  },
  {
    exact: true,
    path: '/signupnowv2/:referralCode?',
    component: lazy(() => import('src/signupMUI6'))
  },
  {
    exact: true,
    path: '/',
    component: () => <Redirect to="/login" />
  },
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/pages/Error404View'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    path: '/login-unprotected',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app',
        component: () => (
          <RedirectLogic
            defaultRedirects={[
              {
                url: '/app/management/FollowUp',
                permissions: [PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_VIEW],
              },
              {
                url: ROUTES.APP_MANAGEMENT_CHAT,
                permissions: [PERMISSIONS_SYSTEM_CODE.CHAT_VIEW],
              },
              {
                url: ROUTES.APP_ACCOUNT,
                permissions: [PERMISSIONS_SYSTEM_CODE.ACCOUNT_GENERAL_SETTINGS_YOUR_INFO],
              },
            ]}
          />
        ),
      },
      {
        exact: true,
        path: ROUTES.APP_ACCOUNT,
        guard: UserPermissionGuard,
        permissions: [
          PERMISSIONS_SYSTEM_CODE.ACCOUNT_GENERAL_SETTINGS_YOUR_INFO,
          PERMISSIONS_SYSTEM_CODE.ACCOUNT_GENERAL_SETTINGS_CAMPAIGNS_TAGS,
          PERMISSIONS_SYSTEM_CODE.ACCOUNT_GENERAL_SETTINGS_OTHER,
          PERMISSIONS_SYSTEM_CODE.ACCOUNT_GENERAL_SETTINGS_PB_CUSTOM_FIELDS,
          PERMISSIONS_SYSTEM_CODE.ACCOUNT_GENERAL_SETTINGS_SUBSCRIPTION,
          PERMISSIONS_SYSTEM_CODE.ACCOUNT_GENERAL_SETTINGS_PASSWORD,
        ],
        component: lazy(() => import('src/views/pages/AccountView'))
      },
      {
        // Texting move: full-page code entry. No permission guard on
        // purpose -- every consultant being moved must be able to reach it,
        // and the page itself shows "nothing to do" for everyone else.
        exact: true,
        path: ROUTES.APP_VERIFY,
        component: lazy(() => import('src/views/pages/VerifyTextingView'))
      },
      {
        exact: true,
        path: ROUTES.APP_EVENT_SETTINGS,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENTS_SETTINGS_VIEW],
        component: lazy(() => import('src/views/pages/PartySettingsView'))
      },
      {
        exact: true,
        path: ROUTES.APP_ORDER_SETTINGS,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_SETTINGS_VIEW_AND_CRUD],
        component: lazy(() => import('src/views/pages/OrderSettingsView'))
      },
      {
        exact: true,
        path: '/app/order-settings/follow-ups/:followUpId',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_SETTINGS_VIEW_AND_CRUD],
        component: lazy(() => import('src/views/pages/OrderSettingsView/TabFollowUpActions/FollowUpDetails'))
      },
      {
        exact: true,
        path: ROUTES.APP_MANAGEMENT_CUSTOMERS,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_VIEW],
        // CON-1532 — selector picks V2 vs V1 based on CONTACTS_PAGE_V2.
        // V1 file (CustomerListView/) is untouched.
        component: lazy(() => import('src/views/management/CustomerListViewSelector'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_MANAGEMENT_CUSTOMERS}/filter-by/category/:filterCategoryId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_VIEW],
        component: lazy(() => import('src/views/management/CustomerListViewSelector'))
      },
      {
        exact: true,
        path: '/app/management/customers/create',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_CREATE],
        component: lazy(() => import('src/views/management/CustomerCreateView'))
      },
      {
        exact: true,
        path: '/app/management/customers/import',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_IMPORT],
        component: lazy(() => import('src/views/management/CustomerImportRouter'))
      },
      {
        exact: true,
        path: '/app/management/customers/import-v2',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_IMPORT_V2],
        component: lazy(() => import('src/views/management/CustomerImportViewV2'))
      },
      {
        exact: true,
        path: '/app/management/customers/import-history',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_IMPORT],
        component: lazy(() => import('src/views/management/CustomerImportView/ResultsAndHistory'))
      },
      {
        exact: true,
        path: '/app/management/customers/:customerId/edit',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_UPDATE],
        component: lazy(() => import('src/views/management/CustomerEditView'))
      },
      {
        exact: true,
        path: '/app/management/customers/:customerId',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_DETAILS_VIEW],
        component: lazy(() => import('src/views/management/CustomerDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/events',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENT_VIEW],
        // pathToRedirect: ROUTES.APP_PERMISSION_DENIED, // use pathToRedirect to redirect to another page
        component: lazy(() => import('src/views/management/PartyListView'))
      },
      {
        exact: true,
        path: '/app/management/events/create',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENT_CREATE],
        component: lazy(() => import('src/views/management/PartyCreateView'))
      },
      {
        exact: true,
        path: '/app/management/events/:partyId/edit',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENT_UPDATE],
        component: lazy(() => import('src/views/management/PartyEditView'))
      },
      {
        exact: true,
        path: '/app/management/events/:partyId',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENT_DETAILS_VIEW],
        // pathToRedirect: ROUTES.APP_PERMISSION_DENIED, // use pathToRedirect to redirect to another page
        component: lazy(() => import('src/views/management/PartyDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/events/smart-actions/:partyId/:actionGroupId/create',
        component: lazy(() => import('src/views/management/PartyDetailsView/SmartActions/SmartActionCreateView'))
      },
      {
        exact: true,
        path: '/app/management/events/smart-actions/:smartActionId',
        component: lazy(() => import('src/views/management/PartyDetailsView/SmartActions/SmartActionDetails'))
      },
      // CON-1487 Phase E — Event Template detail (party scope).
      {
        exact: true,
        path: '/app/management/events/:partyId/event-templates/:templateType',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENT_TEMPLATES_VIEW],
        component: lazy(() => import('src/views/pages/EventTemplatesView/EventTemplateDetailForParty'))
      },
      {
        exact: true,
        path: '/app/event-settings/event-venues',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENTS_SETTINGS_VIEW],
        component: lazy(() => import('src/views/pages/PartySettingsView/TabPartyVenues/TabPartyVenuesListView'))
      },
      {
        exact: true,
        path: '/app/event-settings/event-venues/create',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENTS_SETTINGS_CREATE],
        component: lazy(() => import('src/views/pages/PartySettingsView/TabPartyVenues/PartyVenueCreate'))
      },
      {
        exact: true,
        path: '/app/event-settings/event-venues/:partyVenueId/edit',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENTS_SETTINGS_UPDATE],
        component: lazy(() => import('src/views/pages/PartySettingsView/TabPartyVenues/PartyVenueEdit'))
      },
      {
        exact: true,
        path: '/app/event-settings/event-venues/:partyVenueId',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENTS_SETTINGS_DETAILS_VIEW],
        component: lazy(() => import('src/views/pages/PartySettingsView/TabPartyVenues/PartyVenueDetails'))
      },
      {
        exact: true,
        path: '/app/event-settings/event-types',
        component: lazy(() => import('src/views/pages/PartySettingsView/TabPartyTypes/TabPartyTypesListView'))
      },
      {
        exact: true,
        path: '/app/event-settings/event-types/create',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENTS_SETTINGS_CREATE],
        component: lazy(() => import('src/views/pages/PartySettingsView/TabPartyTypes/PartyTypeCreate'))
      },
      {
        exact: true,
        path: '/app/event-settings/event-types/:partyTypeId/edit',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENTS_SETTINGS_UPDATE],
        component: lazy(() => import('src/views/pages/PartySettingsView/TabPartyTypes/PartyTypeEdit'))
      },
      {
        exact: true,
        path: '/app/event-settings/event-types/:partyTypeId',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENTS_SETTINGS_DETAILS_VIEW],
        component: lazy(() => import('src/views/pages/PartySettingsView/TabPartyTypes/PartyTypeDetails'))
      },
      // CON-1487 Phase E — Event Template detail (partyType scope).
      {
        exact: true,
        path: '/app/event-settings/event-types/:partyTypeId/event-templates/:templateType',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.EVENT_TEMPLATES_VIEW],
        component: lazy(() => import('src/views/pages/EventTemplatesView/EventTemplateDetailForPartyType'))
      },
      // CON-1487 Phase G — Event Template Defaults Admin (list + detail).
      // Dual-gated via requiredPermissions (AND): ADMIN_TOOLS_VIEW AND
      // EVENT_TEMPLATE_DEFAULTS_ADMIN. Backend routes enforce the same.
      {
        exact: true,
        path: '/app/event-templates/admin',
        guard: UserPermissionGuard,
        requiredPermissions: [
          PERMISSIONS_SYSTEM_CODE.ADMIN_TOOLS_VIEW,
          PERMISSIONS_SYSTEM_CODE.EVENT_TEMPLATE_DEFAULTS_ADMIN,
        ],
        component: lazy(() => import('src/views/pages/EventTemplatesView/EventTemplateAdminListView'))
      },
      {
        exact: true,
        path: '/app/event-templates/admin/:companyKey/:templateType/:id',
        guard: UserPermissionGuard,
        requiredPermissions: [
          PERMISSIONS_SYSTEM_CODE.ADMIN_TOOLS_VIEW,
          PERMISSIONS_SYSTEM_CODE.EVENT_TEMPLATE_DEFAULTS_ADMIN,
        ],
        component: lazy(() => import('src/views/pages/EventTemplatesView/EventTemplateAdminDetailView'))
      },
      // Bulk Contact Merge admin tool — gated on CONTACTS_BULK_MERGE_ADMIN
      // only. The permission is dark-shipped and granted per-user via SQL,
      // so it's the sole authority for access. Backend routes enforce the
      // same permission. Intentionally NOT requiring ADMIN_TOOLS_VIEW so
      // the tool can be granted to non-admin-tooling users (e.g., Diana).
      {
        exact: true,
        path: '/app/admin/bulk-contact-merge',
        guard: UserPermissionGuard,
        requiredPermissions: [
          PERMISSIONS_SYSTEM_CODE.CONTACTS_BULK_MERGE_ADMIN,
        ],
        component: lazy(() => import('src/views/pages/BulkContactMergeView'))
      },
      // CON-1532 Phase 9 — Page Column Defaults admin page. AND-gated on
      // ADMIN_TOOLS_VIEW + PAGE_COLUMN_DEFAULTS_ADMIN (matches Event App
      // Templates' admin-page gating pattern).
      {
        exact: true,
        path: ROUTES.APP_ADMIN_PAGE_COLUMN_DEFAULTS,
        guard: UserPermissionGuard,
        requiredPermissions: [
          PERMISSIONS_SYSTEM_CODE.ADMIN_TOOLS_VIEW,
          PERMISSIONS_SYSTEM_CODE.PAGE_COLUMN_DEFAULTS_ADMIN,
        ],
        component: lazy(() => import('src/views/pages/PageColumnDefaultsAdminView')),
      },
      // CON-1488 — Admin TCR Registrations list page. AND-gated on
      // ADMIN_TOOLS_VIEW + TCR_REGISTRATIONS_ADMIN.
      {
        exact: true,
        path: ROUTES.APP_ADMIN_TCR_REGISTRATIONS,
        guard: UserPermissionGuard,
        requiredPermissions: [
          PERMISSIONS_SYSTEM_CODE.ADMIN_TOOLS_VIEW,
          PERMISSIONS_SYSTEM_CODE.TCR_REGISTRATIONS_ADMIN,
        ],
        component: lazy(() => import('src/views/pages/TCRRegistrationsAdminView')),
      },
      {
        exact: true,
        path: ROUTES.APP_CONTACT_SETTINGS,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_SETTINGS_VIEW],
        component: lazy(() => import('src/views/pages/ContactSettingsView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_CONTACT_SETTINGS}/category/create`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_SETTINGS_CATEGORIES_CREATE],
        component: lazy(() => import('src/views/pages/ContactSettingsView/TabCategories/CategoryCreate'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_CONTACT_SETTINGS}/category/:categoryId/edit`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_SETTINGS_CATEGORIES_UPDATE],
        component: lazy(() => import('src/views/pages/ContactSettingsView/TabCategories/CategoryEdit'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_CONTACT_SETTINGS}/category/:categoryId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CONTACT_SETTINGS_CATEGORIES_DETAILS_VIEW],
        component: lazy(() => import('src/views/pages/ContactSettingsView/TabCategories/CategoryDetails'))
      },
      {
        exact: true,
        path: ROUTES.APP_CHAT_SETTINGS,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.MESSAGE_SETTINGS_VIEW],
        component: lazy(() => import('src/views/pages/MessagingSettingsView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_CHAT_SETTINGS}/keyword/create`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.MESSAGE_SETTINGS_KEYWORDS_EDIT],
        component: lazy(() => import('src/views/pages/MessagingSettingsView/TabKeywords/KeywordCreate'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_CHAT_SETTINGS}/keyword/:keywordId/edit`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.MESSAGE_SETTINGS_KEYWORDS_EDIT],
        component: lazy(() => import('src/views/pages/MessagingSettingsView/TabKeywords/KeywordEdit'))
      },
      {
        exact: true,
        path: ROUTES.APP_GROUP_SETTINGS,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.GROUP_SETTINGS_VIEW],
        component: lazy(() => import('src/views/pages/GroupSettingsView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_GROUP_SETTINGS}/group-types/create`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.GROUP_SETTINGS_CREATE],
        component: lazy(() => import('src/views/pages/GroupSettingsView/TabGroupTypes/GroupTypeCreate'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_GROUP_SETTINGS}/group-types/:groupTypeId/edit`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.GROUP_SETTINGS_UPDATE],
        component: lazy(() => import('src/views/pages/GroupSettingsView/TabGroupTypes/GroupTypeEdit'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_GROUP_SETTINGS}/group-types/:groupTypeId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.GROUP_SETTINGS_DETAILS_VIEW],
        component: lazy(() => import('src/views/pages/GroupSettingsView/TabGroupTypes/GroupTypeDetails'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_GROUP_SETTINGS}/group-types/:groupTypeId/:actionGroupId/smart-actions/:smartActionType/create`,
        guard: UserPermissionGuard,
        component: lazy(() => import('src/views/pages/GroupSettingsView/TabGroupTypes/GroupTypeDetails/TabSmartActions/SmartActionCreate'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_GROUP_SETTINGS}/group-types/:groupTypeId/:actionGroupId/smart-actions/:smartActionType/:smartActionId/edit`,
        guard: UserPermissionGuard,
        component: lazy(() => import('src/views/pages/GroupSettingsView/TabGroupTypes/GroupTypeDetails/TabSmartActions/SmartActionDetails'))
      },
      {
        exact: true,
        path: ROUTES.APP_SMART_FIELDS_SETTINGS,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SMART_FIELDS_VIEW],
        component: lazy(() => import('src/views/pages/SmartFieldsSettingsView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_SMART_FIELDS_SETTINGS}/create`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SMART_FIELDS_MANAGE_DEFINITIONS],
        component: lazy(() => import('src/views/pages/SmartFieldsSettingsView/SmartFieldDetails'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_SMART_FIELDS_SETTINGS}/:definitionId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SMART_FIELDS_VIEW],
        component: lazy(() => import('src/views/pages/SmartFieldsSettingsView/SmartFieldDetails'))
      },
      // CON-1597 — Value Lists (reusable dropdown option sets). MANAGE gates
      // the page; admin scope fields inside render behind
      // ADMIN_VALUE_LISTS_EDIT.
      {
        exact: true,
        path: ROUTES.APP_VALUE_LISTS,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.VALUE_LISTS_MANAGE],
        component: lazy(() => import('src/views/pages/ValueListsView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_VALUE_LISTS}/create`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.VALUE_LISTS_MANAGE],
        component: lazy(() => import('src/views/pages/ValueListsView/ValueListDetails'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_VALUE_LISTS}/:listId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.VALUE_LISTS_MANAGE],
        component: lazy(() => import('src/views/pages/ValueListsView/ValueListDetails'))
      },
      // CON-1552 — Share Center (universal share-code entry + Share Sets).
      // VIEW gates the page; Sets UI inside renders behind SHARE_SETS_MANAGE.
      {
        exact: true,
        path: ROUTES.APP_SHARE_CENTER,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SHARE_SETS_VIEW],
        component: lazy(() => import('src/views/pages/ShareCenterView'))
      },
      // CON-2002 — Share Code apply history (the Apply a Code tab's History
      // button). Same gate as the Share Center page.
      {
        exact: true,
        path: ROUTES.APP_SHARE_CENTER_HISTORY,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SHARE_SETS_VIEW],
        component: lazy(() => import('src/views/pages/ShareCenterView/ApplyHistory'))
      },
      {
        exact: false,
        path: `${ROUTES.APP_SHARE_CENTER}/sets/:shareSetId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SHARE_SETS_MANAGE],
        component: lazy(() => import('src/views/pages/ShareCenterView/ShareSetDetails'))
      },
      {
        exact: true,
        path: ROUTES.APP_SMART_LINKS_SETTINGS,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SMART_LINKS_VIEW],
        component: lazy(() => import('src/views/pages/SmartLinksView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_SMART_LINKS_SETTINGS}/create`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SMART_LINKS_CREATE],
        component: lazy(() => import('src/views/pages/SmartLinksView/SmartLinkDetails'))
      },
      {
        exact: false,
        path: `${ROUTES.APP_SMART_LINKS_SETTINGS}/:smartLinkId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SMART_LINKS_VIEW],
        component: lazy(() => import('src/views/pages/SmartLinksView/SmartLinkDetails'))
      },
      {
        exact: true,
        path: ROUTES.APP_SMART_FORMS,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SMART_FORMS_VIEW],
        component: lazy(() => import('src/views/pages/SmartFormsView'))
      },
      {
        exact: false,
        path: `${ROUTES.APP_SMART_FORMS}/:formId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SMART_FORMS_VIEW],
        component: lazy(() => import('src/views/pages/SmartFormsView/SmartFormBuilder'))
      },
      {
        exact: true,
        path: '/app/smart-action-groups/smart-actions/:partyTypeId/:actionGroupId/create',
        component: lazy(() => import('src/views/pages/SmartActionGroupView/SmartActionCreateView'))
      },
      {
        exact: true,
        path: '/app/smart-action-groups/smart-actions/:smartActionId',
        component: lazy(() => import('src/views/pages/SmartActionGroupView/SmartActionDetails'))
      },
      {
        exact: true,
        path: '/app/form-definition/form-lines/:formOwnerType/:formOwnerTypeId/:formDefinitionId/create-image',
        component: lazy(() => import('src/views/pages/FormsDefinitionView/FormDetails/FormLineDefinitionCreateImageView'))
      },
      {
        exact: true,
        path: '/app/form-definition/form-lines/:formOwnerType/:formOwnerTypeId/:formDefinitionId/create-text',
        component: lazy(() => import('src/views/pages/FormsDefinitionView/FormDetails/FormLineDefinitionCreateTextView'))
      },
      {
        exact: true,
        path: '/app/form-definition/form-lines/:formOwnerType/:formOwnerTypeId/:formDefinitionId/create-submitted-image',
        component: lazy(() => import('src/views/pages/FormsDefinitionView/FormDetails/FormLineDefinitionCreateSubmittedImageView'))
      },
      {
        exact: true,
        path: '/app/form-definition/form-lines/:formOwnerType/:formOwnerTypeId/:formDefinitionId/create-submitted-text',
        component: lazy(() => import('src/views/pages/FormsDefinitionView/FormDetails/FormLineDefinitionCreateSubmittedTextView'))
      },
      {
        exact: true,
        path: '/app/form-definition/form-lines/:formOwnerType/:formLineDefinitionId',
        component: lazy(() => import('src/views/pages/FormsDefinitionView/FormDetails/FormLineDefinitionDetails'))
      },
      {
        exact: true,
        path: '/app/form/form-lines/:partyId/:formId/create-image',
        component: lazy(() => import('src/views/management/PartyDetailsView/JoinForm/FormLineCreateImageView'))
      },
      {
        exact: true,
        path: '/app/form/form-lines/:partyId/:formId/create-text',
        component: lazy(() => import('src/views/management/PartyDetailsView/JoinForm/FormLineCreateTextView'))
      },
      {
        exact: true,
        path: '/app/form/form-lines/:partyId/:formId/create-submitted-image',
        component: lazy(() => import('src/views/management/PartyDetailsView/JoinForm/FormLineCreateSubmittedImageView'))
      },
      {
        exact: true,
        path: '/app/form/form-lines/:partyId/:formId/create-submitted-text',
        component: lazy(() => import('src/views/management/PartyDetailsView/JoinForm/FormLineCreateSubmittedTextView'))
      },
      {
        exact: true,
        path: '/app/form/form-lines/:formLineId',
        component: lazy(() => import('src/views/management/PartyDetailsView/JoinForm/FormLineDetails'))
      },
      {
        exact: true,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_VIEW],
        // pathToRedirect: '/404', // use pathToRedirect to use redirect
        path: '/app/management/followUp',
        component: lazy(() => import('src/views/management/FollowUp'))
      },
      {
        exact: true,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_VIEW],
        // pathToRedirect: '/404', // use pathToRedirect to use redirect
        path: '/app/management/orders',
        component: lazy(() => import('src/views/management/OrderListView'))
      },
      {
        exact: true,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_CREATE],
        path: '/app/management/orders/create',
        component: lazy(() => import('src/views/management/OrderCreateView'))
      },
      {
        exact: true,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_DETAILS_VIEW],
        path: '/app/management/orders/:orderId',
        component: lazy(() => import('src/views/management/OrderDetailsView'))
      },
      {
        exact: true,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.ORDER_FOLLOW_UP_UPDATE],
        path: '/app/management/orders/:orderId/edit',
        component: lazy(() => import('src/views/management/OrderEditFullView'))
      },
      {
        exact: true,
        path: [
          ROUTES.APP_MANAGEMENT_CHAT,
          // CON-2014 — before :chatKey, so "short-codes" is never read as a contact id
          `${ROUTES.APP_MANAGEMENT_CHAT}/short-codes/:shortCode?`,
          `${ROUTES.APP_MANAGEMENT_CHAT}/:chatKey`
        ],
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.CHAT_VIEW],
        component: () => <ChatView />
      },
      {
        exact: true,
        path: ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SCHEDULED_MESSAGES_VIEW],
        component: () => <ScheduledMessagesView />
      },
      {
        exact: true,
        path: `${ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES}/follow-up-edit/:orderId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SCHEDULED_MESSAGES_VIEW],
        component: lazy(() => import('src/views/management/ScheduledMessagesView/MessagesEditView/OrderFollowUpMessageEditView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES}/smart-action-edit/:smartActionId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SCHEDULED_MESSAGES_VIEW],
        component: lazy(() => import('src/views/management/ScheduledMessagesView/MessagesEditView/SmartActionMessageEditView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES}/manually-scheduled-edit/:messageId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SCHEDULED_MESSAGES_VIEW],
        component: lazy(() => import('src/views/management/ScheduledMessagesView/MessagesEditView/ManuallyScheduledMessageEditView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES}/birthday-message-edit/:messageId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SCHEDULED_MESSAGES_VIEW],
        component: lazy(() => import('src/views/management/ScheduledMessagesView/MessagesEditView/BirthdayMessageEditView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_MANAGEMENT_SCHEDULED_MESSAGES}/group-smart-action-edit/:messageId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SCHEDULED_MESSAGES_VIEW],
        component: lazy(() => import('src/views/management/ScheduledMessagesView/MessagesEditView/GroupSmartActionMessageEditView'))
      },
      {
        exact: true,
        path: ROUTES.APP_MANAGEMENT_GROUPS,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.GROUP_VIEW],
        component: lazy(() => import('src/views/management/GroupListView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_MANAGEMENT_GROUPS}/create`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.GROUP_CREATE],
        component: lazy(() => import('src/views/management/GroupCreateView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_MANAGEMENT_GROUPS}/:groupId/edit`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.GROUP_UPDATE],
        component: lazy(() => import('src/views/management/GroupEditView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_MANAGEMENT_GROUPS}/:groupId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.GROUP_DETAILS_VIEW],
        component: lazy(() => import('src/views/management/GroupDetailsView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_GROUP_FORM_LINES}/:groupId/:formId/create-image`,
        component: lazy(() => import('src/views/management/GroupDetailsView/JoinForm/FormLineCreateImageView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_GROUP_FORM_LINES}/:groupId/:formId/create-text`,
        component: lazy(() => import('src/views/management/GroupDetailsView/JoinForm/FormLineCreateTextView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_GROUP_FORM_LINES}/:groupId/:formId/create-submitted-image`,
        component: lazy(() => import('src/views/management/GroupDetailsView/JoinForm/FormLineCreateSubmittedImageView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_GROUP_FORM_LINES}/:groupId/:formId/create-submitted-text`,
        component: lazy(() => import('src/views/management/GroupDetailsView/JoinForm/FormLineCreateSubmittedTextView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_GROUP_FORM_LINES}/:formLineId`,
        component: lazy(() => import('src/views/management/GroupDetailsView/JoinForm/FormLineDetails'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_MANAGEMENT_GROUPS}/smart-actions/:groupId/:actionGroupId/create`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.GROUP_DETAILS_VIEW],
        component: lazy(() => import('src/views/management/GroupDetailsView/GroupSmartActions/SmartActionCreateView'))
      },
      {
        exact: true,
        path: `${ROUTES.APP_MANAGEMENT_GROUPS}/smart-actions/:smartActionId`,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.GROUP_DETAILS_VIEW],
        component: lazy(() => import('src/views/management/GroupDetailsView/GroupSmartActions/SmartActionDetails'))
      },
      {
        exact: true,
        path: '/app/email-composer-harness',
        component: lazy(() => import('src/views/management/EmailComposerHarnessView'))
      },
      {
        exact: true,
        path: '/app/management',
        component: () => <Redirect to="/app/management/customers" />
      },
      {
        exact: true,
        path: '/app/management',
        component: () => <Redirect to="/app/management/events" />
      },
      {
        exact: true,
        path: '/app/settings/messages',
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.MESSAGE_TEMPLATES_VIEW_AND_CRUD],
        component: () => <MessageView />
      },
      {
        exact: true,
        path: '/app/settings',
        component: () => <Redirect to="/app/account" />
      },
      {
        exact: true,
        path: '/app/event-settings',
        component: () => <Redirect to="/app/event-settings" />
      },
      {
        exact: true,
        path: '/app/order-settings',
        component: () => <Redirect to="/app/order-settings" />
      },
      {
        exact: true,
        path: ROUTES.APP_HELP_AND_INFORMATION,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.HELP_INFORMATION_VIEW],
        component: () => <HelpAndInformation />
      },
      // CON-1699 — Setup Assistant. Two gates, both must hold, and
      // renderRoutes mounts one guard per route, so they are nested: the
      // outer route runs UserPermissionGuard on SETUP_ASSISTANT (the same
      // code the nav entry and every /api/setup-assistant route check; a
      // miss renders Error403View like every other permission-gated
      // route), then the inner route runs CPTextingGuard, the CP-texting
      // backstop that redirects a non-CP account to /app. Same nesting
      // renderRoutes already does for /app itself, one level down.
      {
        path: ROUTES.APP_SETUP_ASSISTANT,
        guard: UserPermissionGuard,
        permissions: [PERMISSIONS_SYSTEM_CODE.SETUP_ASSISTANT],
        routes: [
          {
            exact: true,
            path: ROUTES.APP_SETUP_ASSISTANT,
            guard: CPTextingGuard,
            component: lazy(() => import('src/views/pages/SetupAssistantView'))
          },
        ],
      },
      {
        exact: true,
        path: ROUTES.APP_PERMISSION_DENIED,
        component: lazy(() => import('src/views/pages/Error403View')),
      },
      {
        exact: true,
        path: ROUTES.APP_404,
        component: lazy(() => import('src/views/pages/Error404View')),
      },
      {
        component: () => <Redirect to={ROUTES.APP_404} />
      }
    ]
  },
  {
    path: '*',
    layout: MainLayout,
    routes: [
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];

const renderRoutes = (routes) => (routes ? (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const {
          guard,
          layout,
          component: Component,
          permissions,
          requiredPermissions,
          pathToRedirect,
        } = route || {};
        const Guard = guard || Fragment;
        const Layout = layout || Fragment;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard
                {...permissions ? { permissions } : {}}
                {...requiredPermissions ? { requiredPermissions } : {}}
                {...pathToRedirect ? { pathToRedirect } : {}}
              >
                <Layout>{route.routes ? renderRoutes(route.routes) : <Component {...props} />}</Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
) : null);

function Routes() {
  return renderRoutes(routesConfig);
}

export default Routes;
